/**
 * Distribution functions. Owner: core.
 * Implements CORE-200 to CORE-206.
 *
 * Accuracy target: agreement with R (pnorm, qnorm, pt, qt, pchisq, qchisq) to about 1e-12
 * relative over the ranges the engine uses, including tail probabilities down to 1e-15 and below.
 *
 * Design notes:
 * - Every CDF has an upper-tail twin. p-values must come from the upper-tail functions:
 *   computing `1 - cdf(x)` loses all relative accuracy once the tail is below about 1e-8.
 * - The normal CDF is Cody's rational Chebyshev algorithm, as in R's pnorm.c.
 * - The normal quantile is Wichura's AS241 (PPND16), as in R's qnorm.c.
 * - Student t uses the regularised incomplete beta (continued fraction, modified Lentz).
 * - Chi-squared uses the regularised incomplete gamma (series below the mode, continued
 *   fraction above it), with Loader's saddle-point form of the prefactor to avoid cancellation.
 * - The t and chi-squared quantiles start from a closed-form approximation and finish with a
 *   bracketed Newton iteration on the log of the relevant tail probability.
 */

const LN_SQRT_2PI = 0.918938533204672741780329736406; // log(sqrt(2 * pi))
const SQRT_32 = 5.656854249492380195206754896838;
const ONE_OVER_SQRT_2PI = 0.398942280401432677939946059934;
const FPMIN = 1e-300;
const CF_EPS = 1e-16;
const MAX_CF_ITER = 100000;

function isBadDf(df: number): boolean {
  return Number.isNaN(df) || df <= 0;
}

// ---------------------------------------------------------------------------
// Normal
// ---------------------------------------------------------------------------

const CODY_A = [2.2352520354606839287, 161.02823106855587881, 1067.6894854603709582, 18154.981253343561249, 0.065682337918207449113] as const;
const CODY_B = [47.20258190468824187, 976.09855173777669322, 10260.932208618978205, 45507.789335026729956] as const;
const CODY_C = [
  0.39894151208813466764, 8.8831497943883759412, 93.506656132177855979, 597.27027639480026226, 2494.5375852903726711,
  6848.1904505362823326, 11602.651437647350124, 9842.7148383839780218, 1.0765576773720192317e-8,
] as const;
const CODY_D = [
  22.266688044328115691, 235.38790178262499861, 1519.377599407554805, 6485.558298266760755, 18615.571640885098091,
  34900.952721145977266, 38912.003286093271411, 19685.429676859990727,
] as const;
const CODY_P = [0.21589853405795699, 0.1274011611602473639, 0.022235277870649807, 0.001421619193227893466, 2.9112874951168792e-5, 0.02307344176494017303] as const;
const CODY_Q = [1.28426009614491121, 0.468238212480865118, 0.0659881378689285515, 0.00378239633202758244, 7.29751555083966205e-5] as const;

/** exp(-x^2/2) computed in two pieces so the rounding of x^2 does not cost relative accuracy. */
function expHalfSquare(x: number): number {
  const xsq = Math.trunc(x * 16) / 16;
  const del = (x - xsq) * (x + xsq);
  return Math.exp(-xsq * xsq * 0.5) * Math.exp(-del * 0.5);
}

/** Both tails of the standard normal: [P(Z <= x), P(Z > x)]. Cody (1969), as in R pnorm_both. */
function normalBoth(x: number): [number, number] {
  if (Number.isNaN(x)) return [NaN, NaN];
  const y = Math.abs(x);
  if (y <= 0.67448975) {
    let xnum = 0;
    let xden = 0;
    if (y > 1.1102230246251565e-16) {
      const xsq = x * x;
      xnum = CODY_A[4] * xsq;
      xden = xsq;
      for (let i = 0; i < 3; i++) {
        xnum = (xnum + CODY_A[i]!) * xsq;
        xden = (xden + CODY_B[i]!) * xsq;
      }
    }
    const temp = (x * (xnum + CODY_A[3])) / (xden + CODY_B[3]);
    return [0.5 + temp, 0.5 - temp];
  }
  let small: number; // P(Z > y)
  if (y <= SQRT_32) {
    let xnum = CODY_C[8] * y;
    let xden = y;
    for (let i = 0; i < 7; i++) {
      xnum = (xnum + CODY_C[i]!) * y;
      xden = (xden + CODY_D[i]!) * y;
    }
    const temp = (xnum + CODY_C[7]) / (xden + CODY_D[7]);
    small = expHalfSquare(y) * temp;
  } else if (y < 38.6) {
    const xsq = 1 / (x * x);
    let xnum = CODY_P[5] * xsq;
    let xden = xsq;
    for (let i = 0; i < 4; i++) {
      xnum = (xnum + CODY_P[i]!) * xsq;
      xden = (xden + CODY_Q[i]!) * xsq;
    }
    let temp = (xsq * (xnum + CODY_P[4])) / (xden + CODY_Q[4]);
    temp = (ONE_OVER_SQRT_2PI - temp) / y;
    small = expHalfSquare(y) * temp;
  } else {
    small = 0;
  }
  return x > 0 ? [1 - small, small] : [small, 1 - small];
}

/** Standard normal CDF, P(Z <= x). */
export function normalCdf(x: number): number {
  return normalBoth(x)[0];
}

/** Standard normal upper tail, P(Z > x), accurate in relative terms for large x. */
export function normalCdfUpper(x: number): number {
  return normalBoth(x)[1];
}

/** Two-sided p-value for a z statistic: 2 P(Z > |z|). */
export function normalTwoSidedP(z: number): number {
  return 2 * normalCdfUpper(Math.abs(z));
}

/** Wichura AS241 core. `p` is the lower-tail probability; `lowTail = min(p, 1 - p)` is passed exactly. */
function ppnd16(p: number, lowTail: number): number {
  const q = p - 0.5;
  if (Math.abs(q) <= 0.425) {
    const r = 0.180625 - q * q;
    return (
      (q *
        (((((((r * 2509.0809287301226727 + 33430.575583588128105) * r + 67265.770927008700853) * r +
          45921.953931549871457) *
          r +
          13731.693765509461125) *
          r +
          1971.5909503065514427) *
          r +
          133.14166789178437745) *
          r +
          3.387132872796366608)) /
      (((((((r * 5226.495278852545925 + 28729.085735721942674) * r + 39307.89580009271061) * r + 21213.794301586595867) * r +
        5394.1960214247511077) *
        r +
        687.1870074920579083) *
        r +
        42.313330701600911252) *
        r +
        1)
    );
  }
  let r = Math.sqrt(-Math.log(lowTail));
  let val: number;
  if (r <= 5) {
    r -= 1.6;
    val =
      (((((((r * 7.7454501427834140764e-4 + 0.0227238449892691845833) * r + 0.24178072517745061177) * r +
        1.27045825245236838258) *
        r +
        3.64784832476320460504) *
        r +
        5.7694972214606914055) *
        r +
        4.6303378461565452959) *
        r +
        1.42343711074968357734) /
      (((((((r * 1.05075007164441684324e-9 + 5.475938084995344946e-4) * r + 0.0151986665636164571966) * r +
        0.14810397642748007459) *
        r +
        0.68976733498510000455) *
        r +
        1.6763848301838038494) *
        r +
        2.05319162663775882187) *
        r +
        1);
  } else {
    r -= 5;
    val =
      (((((((r * 2.01033439929228813265e-7 + 2.71155556874348757815e-5) * r + 0.0012426609473880784386) * r +
        0.026532189526576123093) *
        r +
        0.29656057182850489123) *
        r +
        1.7848265399172913358) *
        r +
        5.4637849111641143699) *
        r +
        6.6579046435011037772) /
      (((((((r * 2.04426310338993978564e-15 + 1.4215117583164458887e-7) * r + 1.8463183175100546818e-5) * r +
        7.868691311456132591e-4) *
        r +
        0.0148753612908506148525) *
        r +
        0.13692988092273580531) *
        r +
        0.59983220655588793769) *
        r +
        1);
  }
  return q < 0 ? -val : val;
}

/** Standard normal quantile (AS241). */
export function normalQuantile(p: number): number {
  if (Number.isNaN(p) || p < 0 || p > 1) return NaN;
  if (p === 0) return -Infinity;
  if (p === 1) return Infinity;
  return ppnd16(p, Math.min(p, 1 - p));
}

/** Upper-tail normal quantile: x with P(Z > x) = q. Exact by symmetry, so small q keeps full accuracy. */
export function normalQuantileUpper(q: number): number {
  return -normalQuantile(q);
}

// ---------------------------------------------------------------------------
// Gamma-function helpers
// ---------------------------------------------------------------------------

/** Stirling-series remainder: lgamma(x) - [(x - 0.5) log x - x + log sqrt(2 pi)], for x >= 10. */
function lgammacorLarge(x: number): number {
  const x2 = 1 / (x * x);
  // Bernoulli terms B_2n / (2n (2n - 1)); truncation error below 1e-18 for x >= 10.
  return (
    (1 / 12 +
      x2 *
        (-1 / 360 +
          x2 * (1 / 1260 + x2 * (-1 / 1680 + x2 * (1 / 1188 + x2 * (-691 / 360360 + x2 * (1 / 156 + x2 * (-3617 / 122400)))))))) /
    x
  );
}

function stirlingLgamma(z: number): number {
  return (z - 0.5) * Math.log(z) - z + LN_SQRT_2PI + lgammacorLarge(z);
}

/** log Gamma(x) for x > 0. Shifts small arguments up to the Stirling range. */
export function logGamma(x: number): number {
  if (Number.isNaN(x) || x <= 0) return NaN;
  if (x >= 10) return stirlingLgamma(x);
  let prod = 1;
  let z = x;
  while (z < 10) {
    prod *= z;
    z += 1;
  }
  return stirlingLgamma(z) - Math.log(prod);
}

/** Loader's stirlerr(n) = log(n!) - log(sqrt(2 pi n) (n / e)^n), for n > 0. */
function stirlerr(n: number): number {
  if (n >= 10) return lgammacorLarge(n);
  return logGamma(n + 1) - (n + 0.5) * Math.log(n) + n - LN_SQRT_2PI;
}

/** Loader's bd0(x, np) = x log(x / np) + np - x, computed without cancellation. */
function bd0(x: number, np: number): number {
  if (Math.abs(x - np) < 0.1 * (x + np)) {
    let v = (x - np) / (x + np);
    let s = (x - np) * v;
    let ej = 2 * x * v;
    v *= v;
    for (let j = 1; j < 1000; j++) {
      ej *= v;
      const s1 = s + ej / (2 * j + 1);
      if (s1 === s) return s1;
      s = s1;
    }
    return s;
  }
  return x * Math.log(x / np) + np - x;
}

/** x^a e^{-x} / Gamma(a + 1), via Loader's saddle-point form (a > 0, x >= 0). */
function poissonKernel(a: number, x: number): number {
  if (x === 0) return 0;
  if (!Number.isFinite(x)) return 0;
  return Math.exp(-stirlerr(a) - bd0(a, x)) / Math.sqrt(2 * Math.PI * a);
}

/** log Beta(a, b), arranged to avoid cancellation when an argument is large (R lbeta.c). */
function logBeta(a: number, b: number): number {
  const p = Math.min(a, b);
  const q = Math.max(a, b);
  if (p >= 10) {
    const corr = lgammacorLarge(p) + lgammacorLarge(q) - lgammacorLarge(p + q);
    return -0.5 * Math.log(q) + LN_SQRT_2PI + corr + (p - 0.5) * Math.log(p / (p + q)) + q * Math.log1p(-p / (p + q));
  }
  if (q >= 10) {
    const corr = lgammacorLarge(q) - lgammacorLarge(p + q);
    return logGamma(p) + corr + p - p * Math.log(p + q) + (q - 0.5) * Math.log1p(-p / (p + q));
  }
  return logGamma(p) + logGamma(q) - logGamma(p + q);
}

// ---------------------------------------------------------------------------
// Regularised incomplete gamma
// ---------------------------------------------------------------------------

/** [P(a, x), Q(a, x)], the lower and upper regularised incomplete gamma functions. */
function incompleteGamma(a: number, x: number): [number, number] {
  if (Number.isNaN(x) || Number.isNaN(a)) return [NaN, NaN];
  if (x <= 0) return [0, 1];
  if (!Number.isFinite(x)) return [1, 0];
  if (x < a + 1) {
    // Series: P = x^a e^-x / Gamma(a+1) * sum_n x^n / ((a+1)...(a+n)).
    let term = 1;
    let sum = 1;
    for (let n = 1; n < MAX_CF_ITER; n++) {
      term *= x / (a + n);
      sum += term;
      if (term < sum * CF_EPS) break;
    }
    const lower = poissonKernel(a, x) * sum;
    return [lower, 1 - lower];
  }
  // Continued fraction for Q (modified Lentz): Q = x^a e^-x / Gamma(a) * 1/(x+1-a- ...).
  let b = x + 1 - a;
  let c = 1 / FPMIN;
  let d = 1 / b;
  let h = d;
  for (let i = 1; i < MAX_CF_ITER; i++) {
    const an = -i * (i - a);
    b += 2;
    d = an * d + b;
    if (Math.abs(d) < FPMIN) d = FPMIN;
    c = b + an / c;
    if (Math.abs(c) < FPMIN) c = FPMIN;
    d = 1 / d;
    const del = d * c;
    h *= del;
    if (Math.abs(del - 1) < CF_EPS) break;
  }
  const upper = a * poissonKernel(a, x) * h;
  return [1 - upper, upper];
}

// ---------------------------------------------------------------------------
// Regularised incomplete beta
// ---------------------------------------------------------------------------

/** Continued fraction for I_x(a, b) (Numerical Recipes betacf, modified Lentz). */
function betaContinuedFraction(a: number, b: number, x: number, y: number): number {
  const qab = a + b;
  const qap = a + 1;
  const qam = a - 1;
  let c = 1;
  // 1 - (a + b) x / (a + 1), written with y = 1 - x so it does not cancel when x is near 1.
  let d = (a * y + 1 - b * x) / qap;
  if (Math.abs(d) < FPMIN) d = FPMIN;
  d = 1 / d;
  let h = d;
  for (let m = 1; m < MAX_CF_ITER; m++) {
    const m2 = 2 * m;
    let aa = (m * (b - m) * x) / ((qam + m2) * (a + m2));
    d = 1 + aa * d;
    if (Math.abs(d) < FPMIN) d = FPMIN;
    c = 1 + aa / c;
    if (Math.abs(c) < FPMIN) c = FPMIN;
    d = 1 / d;
    h *= d * c;
    aa = (-(a + m) * (qab + m) * x) / ((a + m2) * (qap + m2));
    d = 1 + aa * d;
    if (Math.abs(d) < FPMIN) d = FPMIN;
    c = 1 + aa / c;
    if (Math.abs(c) < FPMIN) c = FPMIN;
    d = 1 / d;
    const del = d * c;
    h *= del;
    if (Math.abs(del - 1) < CF_EPS) break;
  }
  return h;
}

/**
 * [I_x(a, b), 1 - I_x(a, b)] where y = 1 - x and the logs of x and y are supplied by the caller,
 * so callers can compute them without cancellation. The smaller tail is always computed directly.
 */
function incompleteBeta(a: number, b: number, x: number, y: number, logx: number, logy: number): [number, number] {
  if (x <= 0) return [0, 1];
  if (y <= 0) return [1, 0];
  const lbeta = logBeta(a, b);
  if (x < (a + 1) / (a + b + 2)) {
    const front = Math.exp(a * logx + b * logy - lbeta) / a;
    const lower = front * betaContinuedFraction(a, b, x, y);
    return [lower, 1 - lower];
  }
  const front = Math.exp(b * logy + a * logx - lbeta) / b;
  const upper = front * betaContinuedFraction(b, a, y, x);
  return [1 - upper, upper];
}

// ---------------------------------------------------------------------------
// Student t
// ---------------------------------------------------------------------------

/** P(T > t) for t >= 0. */
function tUpperNonNegative(t: number, df: number): number {
  if (t === 0) return 0.5;
  if (!Number.isFinite(t)) return 0;
  if (df === Infinity) return normalCdfUpper(t);
  // x = df / (df + t^2), y = t^2 / (df + t^2); P(T > t) = I_x(df/2, 1/2) / 2.
  const r = (t * t) / df;
  let x: number;
  let y: number;
  let logx: number;
  let logy: number;
  if (Number.isFinite(r)) {
    const l1p = Math.log1p(r);
    x = 1 / (1 + r);
    y = r / (1 + r);
    logx = -l1p;
    logy = Math.log(r) - l1p;
  } else {
    // t^2 overflowed: work with logs directly.
    logx = Math.log(df) - 2 * Math.log(t);
    x = Math.exp(logx);
    y = 1;
    logy = 0;
  }
  return 0.5 * incompleteBeta(df / 2, 0.5, x, y, logx, logy)[0];
}

/** Student t CDF with df degrees of freedom, P(T <= x). */
export function tCdf(x: number, df: number): number {
  if (Number.isNaN(x) || isBadDf(df)) return NaN;
  return x >= 0 ? 1 - tUpperNonNegative(x, df) : tUpperNonNegative(-x, df);
}

/** Student t upper tail, P(T > x). */
export function tCdfUpper(x: number, df: number): number {
  if (Number.isNaN(x) || isBadDf(df)) return NaN;
  return x >= 0 ? tUpperNonNegative(x, df) : 1 - tUpperNonNegative(-x, df);
}

/** Two-sided p-value for a t statistic: 2 P(T > |t|). */
export function tTwoSidedP(t: number, df: number): number {
  if (Number.isNaN(t) || isBadDf(df)) return NaN;
  return 2 * tUpperNonNegative(Math.abs(t), df);
}

function tLogDensity(t: number, df: number): number {
  return -logBeta(df / 2, 0.5) - 0.5 * Math.log(df) - ((df + 1) / 2) * Math.log1p((t * t) / df);
}

/** Hill (1970) approximation to the upper-tail t quantile, used only as a starting value. */
function hillStart(q: number, n: number): number {
  const P = 2 * q;
  const a = 1 / (n - 0.5);
  const b = 48 / (a * a);
  let c = ((((20700 * a) / b - 98) * a - 16) * a + 96.36);
  const d = ((94.5 / (b + c) - 3) / b + 1) * Math.sqrt((a * Math.PI) / 2) * n;
  let y = Math.pow(d * P, 2 / n);
  if (y > 0.05 + a) {
    const x = normalQuantileUpper(0.5 * P);
    y = x * x;
    if (n < 5) c += 0.3 * (n - 4.5) * (x + 0.6);
    c = (((0.05 * d * x - 5) * x - 7) * x - 2) * x + b + c;
    y = (((((0.4 * y + 6.3) * y + 36) * y + 94.5) / c - y - 3) / b + 1) * x;
    y = Math.expm1(a * y * y);
  } else {
    y = ((1 / ((((n + 6) / (n * y) - 0.089 * d - 0.822) * (n + 2) * 3)) + 0.5 / (n + 4)) * y - 1) * ((n + 1) / (n + 2)) + 1 / y;
  }
  return Math.sqrt(n * y);
}

/**
 * Bracketed Newton iteration on h(x) = log(tail(x)) - log(target) over (lo, hi), with
 * geometric or arithmetic bisection whenever a Newton step leaves the bracket.
 * `increasing` says whether tail(x) increases with x. `logDensity` is the log of |d tail / dx|.
 */
function solveTail(
  tail: (x: number) => number,
  logDensity: (x: number) => number,
  target: number,
  increasing: boolean,
  start: number,
  lowerBound: number,
): number {
  const logTarget = Math.log(target);
  let lo = lowerBound;
  let hi = Infinity;
  let x = start > lowerBound && Number.isFinite(start) ? start : lowerBound + 1;
  for (let iter = 0; iter < 400; iter++) {
    const f = tail(x);
    const h = Math.log(f) - logTarget;
    if (h === 0) return x;
    // tooLow means the root lies above x.
    const tooLow = increasing ? h < 0 : h > 0;
    if (tooLow) lo = x;
    else hi = x;
    let next: number;
    if (Number.isFinite(h) && f > 0) {
      const slope = Math.exp(logDensity(x) - Math.log(f)) * (increasing ? 1 : -1);
      next = x - h / slope;
    } else {
      next = NaN;
    }
    if (!(next > lo && next < hi) || !Number.isFinite(next)) {
      if (hi === Infinity) next = Math.max(2 * x, x + 1);
      else if (lo > 0 && hi / lo > 4) next = Math.sqrt(lo * hi);
      else next = 0.5 * (lo + hi);
    }
    if (Math.abs(next - x) <= 4e-16 * Math.abs(x) || next === lo || next === hi) return next;
    x = next;
  }
  return x;
}

/** Student t quantile. */
export function tQuantile(p: number, df: number): number {
  if (Number.isNaN(p) || isBadDf(df) || p < 0 || p > 1) return NaN;
  if (p === 0) return -Infinity;
  if (p === 1) return Infinity;
  if (p === 0.5) return 0;
  const q = p < 0.5 ? p : 1 - p; // exact for p >= 0.5
  const t = tQuantileUpperTail(q, df);
  return p < 0.5 ? -t : t;
}

/** Upper-tail t quantile: t with P(T > t) = q, for 0 < q < 1. */
export function tQuantileUpper(q: number, df: number): number {
  return -tQuantile(q, df);
}

function tQuantileUpperTail(q: number, df: number): number {
  // Here 0 < q < 0.5 and the result is positive.
  if (df === Infinity || df > 1e8) {
    const z = normalQuantileUpper(q);
    if (df === Infinity) return z;
    return solveTail((t) => tUpperNonNegative(t, df), (t) => tLogDensity(t, df), q, false, z, 0);
  }
  if (df === 1) {
    // Cauchy: exact closed form; tan(pi (0.5 - q)) = 1 / tan(pi q).
    return 1 / Math.tan(Math.PI * q);
  }
  if (df === 2) {
    // Exact: t = (1 - 2q) / sqrt(2 q (1 - q)).
    return (1 - 2 * q) / Math.sqrt(2 * q * (1 - q));
  }
  let start = df >= 1 ? hillStart(q, df) : normalQuantileUpper(q);
  if (!(start > 0) || !Number.isFinite(start)) start = Math.max(1, normalQuantileUpper(q));
  return solveTail((t) => tUpperNonNegative(t, df), (t) => tLogDensity(t, df), q, false, start, 0);
}

// ---------------------------------------------------------------------------
// Chi-squared
// ---------------------------------------------------------------------------

/** Chi-squared CDF, P(X <= x). */
export function chiSquaredCdf(x: number, df: number): number {
  if (Number.isNaN(x) || isBadDf(df)) return NaN;
  if (x <= 0) return 0;
  return incompleteGamma(df / 2, x / 2)[0];
}

/** Chi-squared upper tail, P(X > x). Use this for p-values. */
export function chiSquaredCdfUpper(x: number, df: number): number {
  if (Number.isNaN(x) || isBadDf(df)) return NaN;
  if (x <= 0) return 1;
  return incompleteGamma(df / 2, x / 2)[1];
}

function chiSquaredLogDensity(x: number, df: number): number {
  const a = df / 2;
  // f(x) = a * poissonKernel(a, x/2) / x
  return Math.log(a) - stirlerr(a) - bd0(a, x / 2) - 0.5 * Math.log(2 * Math.PI * a) - Math.log(x);
}

/**
 * Starting value for the chi-squared quantile: Wilson-Hilferty, or for a small lower tail the
 * leading term P(a, x/2) ~ (x/2)^a / Gamma(a + 1). Whichever is closer on the log-probability
 * scale wins; the bracketed solver makes either safe.
 */
function chiSquaredStart(p: number, upper: boolean, df: number): number {
  const z = upper ? normalQuantileUpper(p) : normalQuantile(p);
  const c = 2 / (9 * df);
  const candidates: number[] = [];
  const wh = df * Math.pow(1 - c + z * Math.sqrt(c), 3);
  if (wh > 0 && Number.isFinite(wh)) candidates.push(wh);
  if (!upper) {
    const a = df / 2;
    const small = 2 * Math.exp((Math.log(p) + logGamma(a + 1)) / a);
    if (small > 0 && Number.isFinite(small)) candidates.push(small);
  }
  if (candidates.length === 0) return Math.max(df, 1);
  const logP = Math.log(p);
  let best = candidates[0]!;
  let bestErr = Infinity;
  for (const x of candidates) {
    const tail = upper ? chiSquaredCdfUpper(x, df) : chiSquaredCdf(x, df);
    const err = Math.abs(Math.log(tail) - logP);
    if (err < bestErr) {
      bestErr = err;
      best = x;
    }
  }
  return best;
}

/** Chi-squared quantile, x with P(X <= x) = p. */
export function chiSquaredQuantile(p: number, df: number): number {
  if (Number.isNaN(p) || isBadDf(df) || p < 0 || p > 1) return NaN;
  if (p === 0) return 0;
  if (p === 1) return Infinity;
  if (p > 0.5) return chiSquaredQuantileUpper(1 - p, df); // 1 - p is exact here
  const start = chiSquaredStart(p, false, df);
  return solveTail((x) => chiSquaredCdf(x, df), (x) => chiSquaredLogDensity(x, df), p, true, start, 0);
}

/** Upper-tail chi-squared quantile, x with P(X > x) = q. Accurate for small q. */
export function chiSquaredQuantileUpper(q: number, df: number): number {
  if (Number.isNaN(q) || isBadDf(df) || q < 0 || q > 1) return NaN;
  if (q === 0) return Infinity;
  if (q === 1) return 0;
  if (q > 0.5) return chiSquaredQuantile(1 - q, df);
  const start = chiSquaredStart(q, true, df);
  return solveTail((x) => chiSquaredCdfUpper(x, df), (x) => chiSquaredLogDensity(x, df), q, false, start, 0);
}
